import React, {useState, useEffect, useCallback, useRef} from 'react';
import Swal from 'sweetalert2';

import useApi from '../../../utils/useApi';
import {Link, useSearchParams, useNavigate} from "react-router-dom";
import {Search, ArrowUp, ArrowDown} from "lucide-react";
import {useNavVisibility} from "../../NavVisibilityContext/NavVisibilityContext";
import SelectTaxYear from "../SelectTaxYear/SelectTaxYear";

const ClientDashboard = ({ handleShowHeader, handleShowSettingsHeader }) => {
	const { setIsNavVisible } = useNavVisibility();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const [availableYears, setAvailableYears] = useState([]);

	// Year States
	// Get current year for the year selector range
	const currentYear = new Date().getFullYear();
	// Default to previous tax year (current year - 1)
	const defaultYear = (currentYear - 1).toString();
	const [selectedYear, setSelectedYear] = useState(() => {
		// First check URL params
		const urlYear = searchParams.get('year');
		if (urlYear) return urlYear;

		// Then check localStorage
		const storedYear = localStorage.getItem('taxYear');
		if (storedYear) return storedYear;

		// Finally fall back to default
		return defaultYear;
	});

	// Generate year options from 2022 to current year
	const yearOptions = Array.from(
		{ length: currentYear - 2021 },
		(_, i) => (2022 + i).toString()
	);

	// Initialize states with URL parameters
	const [clients, setClients] = useState([]);
	const [totalClients, setTotalClients] = useState(null);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const [errors, setErrors] = useState([]);

	// Sorting states
	const [sortField, setSortField] = useState(null);
	const [sortDirection, setSortDirection] = useState(null);

	const [isArchiveView, setIsArchiveView] = useState(false);

	const [viewType, setViewType] = useState(searchParams.get('type'));
	const [viewTypeLabel, setViewTypeLabel] = useState(searchParams.get('typeLabel'));
	const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

	const searchTimeout = useRef(null);
	const api = useApi();
	const isMounted = useRef(true);
	const fetchingRef = useRef(false);
	const fetchIdRef = useRef(0);

	// Existing fetch and handler functions remain the same
	const fetchClients = useCallback(async (pageNum, year = selectedYear, search = searchTerm, sDirection = null, sField = null) => {
		if (fetchingRef.current || (pageNum !== 1 && !hasMore)) return;

		const fetchId = ++fetchIdRef.current;

		try {
			fetchingRef.current = true;
			setIsLoading(true);

			const data = {
				page: pageNum,
				type: viewType,
				year: year,
				archive: isArchiveView ? 1 : 0,
				term: search,
				orderBy: sField ? sField : sortField,
				orderDir: sDirection ? sDirection : sortDirection
			}

			const response = await api.getClients(data);

			// Discard stale results if a newer fetch was initiated
			if (fetchId !== fetchIdRef.current) return;

			setTotalClients(response.totalCount);
			setAvailableYears(response.availableYears);

			if (response.users) {
				if (pageNum === 1) {
					setClients(response.users);
				} else {
					setClients(prev => [...prev, ...response.users]);
				}
				setHasMore(response.hasMore);
				setPage(pageNum + 1);
			}
		}
		catch (error) {
			if (fetchId !== fetchIdRef.current) return;
			setErrors(prev => [...prev, error.message]);
		}
		finally {
			if (fetchId === fetchIdRef.current) {
				setIsLoading(false);
				fetchingRef.current = false;
			}
		}
	}, [api, hasMore, isArchiveView, viewType, searchTerm, sortField, sortDirection]);

	// Handle sort toggle
	const handleSort = (field) => {
		let newDirection = null;
		// If clicking on the same field, toggle direction
		if (field === sortField) {
			if(!sortDirection) {
				newDirection = 'asc';
			} else {
				newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
			}

			setSortDirection(newDirection);
		} else {
			newDirection = 'asc';
			// If clicking on a different field, set it as the new sort field and default to ascending
			setSortField(field);
			setSortDirection('asc');
		}

		// Reset page and fetch clients with new sorting
		setPage(1);
		setHasMore(true);
		setClients([]); // Clear existing clients
		fetchClients(1, selectedYear, searchTerm, newDirection, field);
	};

	// Handle search input
	const handleSearch = (event) => {
		const value = event.target.value;
		setSearchTerm(value);
		setHasMore(false);             // Prevent scroll handler from triggering stale fetches
		fetchIdRef.current++;          // Invalidate any in-flight fetch results
		fetchingRef.current = false;   // Allow new fetch to start

		// Clear existing timeout
		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		// Set new timeout
		searchTimeout.current = setTimeout(() => {
			setPage(1);
			setHasMore(true);
			fetchClients(1, selectedYear, value);
		}, 300);
	};

	useEffect(() => {
		setIsNavVisible(true);
		handleShowHeader(true);
		handleShowSettingsHeader(false);
		setPage(1);
		setHasMore(true);
		fetchClients(1);

		return () => {
			isMounted.current = false;
		};
	}, [isArchiveView]);

	// Scroll handler remains the same
	useEffect(() => {
		let timeoutId;

		const handleScroll = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			// Get viewport height
			const viewportHeight = window.innerHeight;
			// Get total scrollable height
			const totalHeight = document.documentElement.scrollHeight;
			// Get current scroll position from top
			const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

			// Calculate distance from bottom (in pixels)
			const distanceFromBottom = totalHeight - (scrollPosition + viewportHeight);

			// If we're within 2000px of the bottom and not already loading and there's more to load
			if (distanceFromBottom < 2000 && !isLoading && hasMore) {
				fetchClients(page);
			}

		};

		// Initial check in case the content doesn't fill the page
		handleScroll();

		// Add scroll listener
		window.addEventListener('scroll', handleScroll);

		// Cleanup
		return () => {
			window.removeEventListener('scroll', handleScroll);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [fetchClients, page, isLoading, hasMore]);

	const handleArchiveToggle = async (clientId, isArchived, clientName) => {
		try {
			const result = await Swal.fire({
				                               title: 'Are you sure?',
				                               text: `Do you want to ${isArchived ? 'unarchive' : 'archive'} client ${clientName}?`,
				                               icon: 'question',
				                               showCancelButton: true,
				                               confirmButtonColor: '#3085d6',
				                               cancelButtonColor: '#d33',
				                               confirmButtonText: isArchived ? 'Yes, unarchive!' : 'Yes, archive!',
				                               cancelButtonText: 'Cancel'
			                               });

			if (result.isConfirmed) {
				let response;
				if (isArchived) {
					response = await api.unarchiveClient(clientId);
				} else {
					response = await api.archiveClient(clientId);
				}

				// Show success message
				await Swal.fire({
					                title: 'Success!',
					                text: response.message,
					                icon: 'success',
					                timer: 2000,
					                showConfirmButton: false
				                });

				setSuccessMessage(response.message);

				setPage(1);
				setHasMore(true);
				fetchClients(1, selectedYear, searchTerm);
				startMessageTimer();
			}
		}
		catch (error) {
			// Show error message
			await Swal.fire({
				                title: 'Error!',
				                text: error.message,
				                icon: 'error'
			                });
			setErrors(prev => [...prev, error.message]);
			startMessageTimer();

		}
	};

	const handleDelete = async (clientId, clientName) => {
		try {
			const result = await Swal.fire({
				                               title: 'Are you sure?',
				                               text: `${clientName} is about to be completely deleted. This action cannot be undone.`,
				                               icon: 'warning',
				                               showCancelButton: true,
				                               confirmButtonColor: '#d33',
				                               cancelButtonColor: '#3085d6',
				                               confirmButtonText: 'Yes, delete!',
				                               cancelButtonText: 'Cancel'
			                               });

			if (result.isConfirmed) {
				await api.deleteClient(clientId);

				// Show success message
				await Swal.fire({
					                title: 'Deleted!',
					                text: 'Client has been successfully deleted.',
					                icon: 'success',
					                timer: 2000,
					                showConfirmButton: false
				                });

				setSuccessMessage('Client successfully deleted');
				setClients(prev => prev.filter(client => client.id !== clientId));
			}
		}
		catch (error) {
			// Show error message
			await Swal.fire({
				                title: 'Error!',
				                text: error.message,
				                icon: 'error'
			                });
			setErrors(prev => [...prev, error.message]);
		}
	};

	const handleChangeYear = (year) => {
		setSelectedYear(year);
		setPage(1);
		setHasMore(true);
		setClients([]); // Clear existing clients
		fetchClients(1, year); // Fetch first page with new year
	}

	const startMessageTimer = () => {
		setTimeout(() => {
			setSuccessMessage(null);
			setErrors([]);
		}, 2700);
	}

	const toggleArchiveView = () => setIsArchiveView(prev => !prev);

	return (
		<div className="p-4 md:p-6 bg-white dark:bg-gray-900 min-h-screen page-container z-10 relative">
			<div className="sticky top-0 z-10 bg-white dark:bg-gray-900 pt-4 pb-2 mb-4">
				<div className="max-w-7xl mx-auto">
					<div className="relative">
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<Search className="h-5 w-5 text-gray-400"/>
						</div>
						<input
							type="text"
							className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white dark:bg-gray-800 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white"
							placeholder="Search clients..."
							value={searchTerm}
							onChange={handleSearch}
						/>
					</div>
				</div>
			</div>
			<div className="max-w-7xl mx-auto">
				{/* Header Section */}
				<header className="mb-6">
					<div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
						<div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
							<h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">
								<span className={`capitalize`}>{viewTypeLabel ?? ''}</span> {isArchiveView ? 'Archived Clients' : 'Active Clients'}
								<span className="text-gray-500 dark:text-gray-400 ml-2">
                                    {totalClients}
                                </span>
							</h3>
							<button
								onClick={toggleArchiveView}
								className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
							>
								View {isArchiveView ? 'Active' : 'Archived'}
							</button>


						</div>
						<div className="flex flex-col md:items-center space-y-4 md:space-y-0 md:space-y-4">
							<div className="flex items-center space-x-2 ">
								<label htmlFor="yearSelect" className="text-sm text-gray-600 dark:text-gray-400 min-w-[70px]">
									Tax Year:
								</label>
								<SelectTaxYear availableYears={availableYears}
								               selectedYear={selectedYear}
								               onSelectYear={handleChangeYear}/>
							</div>
							<Link to={`/new-client`} className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors duration-200">
								Add New Client
								<svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
								</svg>
							</Link>
						</div>

					</div>
				</header>

				{/* Messages */}
				{successMessage && (
					<div className="mb-4 p-4 bg-green-500 text-white rounded-lg">
						<p className="font-semibold">{successMessage}</p>
					</div>
				)}

				{errors.length > 0 && errors.map((error, index) => (
					<div key={index} className="mb-4 p-4 bg-red-500 text-white rounded-lg">
						<p className="font-semibold">
							<strong>Oops!</strong> {error}
						</p>
					</div>
				))}

				{/* Client List */}
				<div className="bg-white dark:bg-gray-800 rounded-lg shadow">
					{/* Desktop View - Table Header */}
					<div className="hidden md:grid md:grid-cols-12 bg-gray-50 dark:bg-gray-700 p-4 rounded-t-lg">
						<div
							className="col-span-4 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase flex items-center cursor-pointer"
							onClick={() => handleSort('client')}
						>
							Client Name
							{sortField === 'client' && (
								<span className="ml-1">
                                    {sortDirection === 'asc' ? (
	                                    <ArrowDown className="h-4 w-4"/>
                                    ) : (
	                                     <ArrowUp className="h-4 w-4"/>
                                     )}
                                </span>
							)}
						</div>
						<div className="col-span-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
							Primary Phone
						</div>
						<div className="col-span-5 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
							Actions
						</div>
					</div>

					<div className="flex sm:hidden md:grid-cols-12 bg-gray-50 dark:bg-gray-700 p-4 rounded-t-lg">
						<div
							className="col-span-4 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase flex items-center cursor-pointer"
							onClick={() => handleSort('client')}
						>
							Client Name
							{sortField === 'client' && (
								<span className="ml-1">
                                    {sortDirection === 'asc' ? (
	                                    <ArrowDown className="h-4 w-4"/>
                                    ) : (
	                                     <ArrowUp className="h-4 w-4"/>
                                     )}
                                </span>
							)}
						</div>
					</div>

					{/* Client Items */}
					<div className="divide-y divide-gray-200 dark:divide-gray-600">
						{clients.map((client) => (
							<div key={client.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
								{/* Mobile & Desktop Layout */}
								<div className="grid md:grid-cols-12 gap-4">
									{/* Client Name */}
									<div className="md:col-span-4">
										<div className="md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1 flex items-center">
											<span>Client Name</span>
										</div>
										<Link
											to={`/client-progress/${client.id}?year=${selectedYear}`}
											className="text-blue-600 dark:text-blue-400 hover:underline"
										>
											{client.profile.client}
										</Link>
									</div>

									{/* Phone Number */}
									<div className="md:col-span-3">
										<div className="md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
											Primary Phone
										</div>
										<span className="inline-block px-2 py-1 text-sm bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                            {client.profile.phone_primary}
                                        </span>
									</div>

									{/* Actions */}
									<div className="md:col-span-5">
										<div className="md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
											Actions
										</div>
										<div className="flex flex-wrap gap-2">
											<Link to={`/client/${client.id}/edit`} className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
												<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
												</svg>
												Update
											</Link>

											{client.is_archived === 1 && (
												<button
													onClick={() => handleDelete(client.id, client.profile.client)}
													className="inline-flex items-center px-3 py-1 border border-red-300 dark:border-red-600 rounded-md text-sm font-medium text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900"
												>
													<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
													</svg>
													Delete
												</button>
											)}

											<button
												onClick={() => handleArchiveToggle(
													client.id,
													client.is_archived,
													client.profile.client
												)}
												className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
											>
												<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													{client.is_archived ? (
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z"/>
													) : (
														 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
													 )}
												</svg>
												{client.is_archived ? 'Unarchive' : 'Archive'}
											</button>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Loading Spinner */}
				{isLoading && (
					<div className="text-center py-4">
						<div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"/>
					</div>
				)}
			</div>
		</div>
	);
};

export default ClientDashboard;
